import { useMemo } from 'react';

import { dateHelpers } from '@nocowanie/core/helpers';

import { MAP_DEFAULTS } from '@app/consts';
import { ProvisionApiMapping } from '@app/enums';
import { mapFilterValues } from '@app/helpers/filter-mapping.helpers';
import { useMapSearch } from '@app/hooks/useMapSearch';
import { AccommodationItemModel, SelectedAccommodationType } from '@app/models';
import { Currency, ProvisionType } from '@app/models/api/api-types';
import { MapAccommodationModel } from '@app/models/elastic-search';

interface UseMapAccommodationMapperProps {
    accommodationData?: MapAccommodationModel;
    popoverData: SelectedAccommodationType;
}

export function useMapAccommodationMapper({
    accommodationData,
    popoverData,
}: UseMapAccommodationMapperProps): AccommodationItemModel | null {
    const {
        searchCriteria: { checkInDate, checkOutDate, guests, flexDates },
        filters: { provision, reservationFeatures },
    } = useMapSearch();

    const advancedFilters = useMemo(
        () => ({
            freeCancel: reservationFeatures?.includes('freeCancellation') ? true : undefined,
            withoutPrepayment: reservationFeatures?.includes('withoutPrepayment')
                ? true
                : undefined,
            provision: provision?.length
                ? (mapFilterValues<number, string>(
                      provision.map(pro => parseInt(pro)),
                      ProvisionApiMapping,
                  ) as ProvisionType[])
                : undefined,
        }),
        [provision, reservationFeatures],
    );

    const checkIn = useMemo(() => {
        return checkInDate ? new Date(checkInDate) : dateHelpers.defaultCheckInDate();
    }, [checkInDate]);

    const checkOut = useMemo(() => {
        return checkOutDate ? new Date(checkOutDate) : dateHelpers.defaultCheckOutDate();
    }, [checkOutDate]);

    const guestCounts = useMemo(
        () => ({
            adultsCount: guests?.adultsCount ?? 2,
            childrenCount: guests?.childrenCount ?? 0,
            childrenAges: guests?.childrenAges ?? [],
        }),
        [guests],
    );

    return useMemo(() => {
        if (!accommodationData) return null;

        const nightsCount = Math.max(1, dateHelpers.daysDateDiff(checkIn, checkOut));

        return {
            id: accommodationData.id,
            name: accommodationData.name,
            path: accommodationData.path || '',
            images: (accommodationData.images ?? []).slice(0, MAP_DEFAULTS.MAX_IMAGES_COUNT),
            settlementName: accommodationData.settlement?.name || '',
            distanceFromCityCentre: null,
            filters: advancedFilters,
            ratingsAverage: accommodationData.ratingsAverage,
            ratingDescription: accommodationData.ratingDescription,
            ratingsCount: accommodationData.ratingCount ?? 0,
            cheapestRate: {
                rate: popoverData?.rate ?? 0,
                baseRate: popoverData?.baseRate ?? 0,
                guests: guestCounts.adultsCount,
                children: guestCounts.childrenCount,
                childrenAges: guestCounts.childrenAges,
                duration: nightsCount,
                currency: popoverData?.currency ?? Currency.Pln,
                checkIn: popoverData?.cheapestRateCheckIn,
                checkOut: popoverData?.cheapestRateCheckOut,
            },
            checkInDate: checkIn,
            checkOutDate: checkOut,
            reservationType: popoverData?.reservationType,
            isUnavailable: popoverData?.isUnavailable,
            additionalFilters: {
                flexDates,
            },
        };
    }, [
        accommodationData,
        checkIn,
        checkOut,
        advancedFilters,
        popoverData?.rate,
        popoverData?.baseRate,
        popoverData?.currency,
        popoverData?.cheapestRateCheckIn,
        popoverData?.cheapestRateCheckOut,
        popoverData?.reservationType,
        popoverData?.isUnavailable,
        guestCounts.adultsCount,
        guestCounts.childrenCount,
        guestCounts.childrenAges,
        flexDates,
    ]);
}
